import { type FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { RadioButton } from "@root/shared/components/RadioButton";
import { SelectBox } from "@root/shared/components/SelectBox";
import { TextArea } from "@root/shared/components/TextArea";
import { Checkbox } from "@root/shared/components/Checkbox";
import { Label } from "@root/shared/components/Label";
import { type InputNotAdoptedDetailViewProps } from "@clientFrontend/features/entry/components/NotAdopted/type";
import { Notice } from "./uiParts/Notice";
import * as styles from "./styles.css";

export const SpView: FC<InputNotAdoptedDetailViewProps> = ({
  completedInterview,
  isPayPerJobEntryFeeType,
  handleRadioButtonChange,
  isChecked,
  handleChangeCheckbox,
  notAdoptedDescriptionTextLength,
  handleTextAreaChange,
  recruitmentFeePath,
  selectBoxOptions,
  selectedReason,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  // MEMO: iOSの場合、チェックボックス、ラジオボタンのバリデーションエラー時にフォーカスされない
  //       そのためバリデーションエラーを検知した場合に手動でフォーカスさせる
  //       ref: https://zenn.dev/daisuke7924/scraps/fe9fdcb57ac53b
  useEffect(() => {
    // 「注意事項に同意する」項目は「メール内容」項目の上なので「メール内容」項目以降でのみ
    // バリデーションエラーが発生したら注意事項に同意する」項目に遷移
    const isNoteFocused =
      errors.note &&
      !errors.completed_interview &&
      !errors.not_adopted_reason &&
      !errors.not_adopted_description;

    // 「面接・見学」項目は一番上の項目なので「面接・見学」でバリデーションエラーが発生したら「面接・見学」にフォーカス
    if (errors.completed_interview) {
      // TODO: react-hook-form v7.50.0 で errors の ref に scrollIntoView() を使用できるのでバージョンアップ後対応を行う
      //       ref: https://github.com/react-hook-form/react-hook-form/pull/11309
      const radioButtonForm = document.getElementById("not-adopted-form-radio-button");
      radioButtonForm?.scrollIntoView({
        block: "center",
        behavior: "auto",
      });
    } else if (isNoteFocused) {
      // TODO: react-hook-form v7.50.0 で errors の ref に scrollIntoView() を使用できるのでバージョンアップ後対応を行う
      //       ref: https://github.com/react-hook-form/react-hook-form/pull/11309
      const checkBoxForm = document.getElementById("not-adopted-form-checkbox");
      checkBoxForm?.scrollIntoView({
        block: "center",
        behavior: "auto",
      });
    }
    // 全ての errors を dependencies 設定すると意図せずスクロールする可能性があるためdisable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.completed_interview, errors.note]);

  return (
    <div className={styles.form}>
      <div className={styles.formItemBox}>
        <h3 className={styles.formItem}>
          面接・見学
          <Label theme="lightPink" className={styles.formRequiredLabel}>
            必須
          </Label>
        </h3>
        <div className={styles.formRadioButtonBox}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={styles.formRadioButton}>
            <RadioButton
              {...register("completed_interview", {
                required: "選択してください。",
              })}
              id="not-adopted-form-radio-button"
              checked={completedInterview === "true"}
              hasError={!!errors.completed_interview}
              onChange={() => handleRadioButtonChange("true")}
              value="true"
            />
            <span className={styles.formRadioLabel}>実施した</span>
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={styles.formRadioButton}>
            <RadioButton
              {...register("completed_interview", {
                required: "選択してください。",
              })}
              checked={completedInterview === "false"}
              hasError={!!errors.completed_interview}
              onChange={() => handleRadioButtonChange("false")}
              value="false"
            />
            <span className={styles.formRadioLabel}>実施していない</span>
          </label>
        </div>
        {errors.completed_interview && (
          <p className={styles.radioErrorMessage}>{errors.completed_interview.message}</p>
        )}
      </div>
      <div className={styles.formItemBox}>
        <h3 className={styles.formItem}>
          不採用理由
          <Label theme="lightPink" className={styles.formRequiredLabel}>
            必須
          </Label>
        </h3>
        <div className={styles.formInputBox}>
          <SelectBox
            {...register("not_adopted_reason", {
              required: "選択してください。",
            })}
            hasError={!!errors.not_adopted_reason}
            selected={!!selectedReason}
            options={selectBoxOptions}
            placeholder="不採用理由をご選択ください"
          />
          {errors.not_adopted_reason && (
            <p className={styles.errorMessage}>{errors.not_adopted_reason.message}</p>
          )}
        </div>
      </div>
      <div className={styles.formItemBox}>
        <h3 className={styles.formItem}>
          不採用理由の詳細
          <Label theme="lightGray" className={styles.formOptionalLabel}>
            任意
          </Label>
        </h3>
        <div className={styles.formInputBox}>
          <p className={styles.formDetailNote}>不採用にされた理由の詳細をお知らせください。</p>
          <TextArea
            {...register("not_adopted_description", {
              maxLength: {
                value: 100,
                message: "100文字以内で入力してください。",
              },
            })}
            placeholder={"何か問題のある方でしたか？\n例）面接に来なかった。連絡が付かなくなった。"}
            hasError={!!errors.not_adopted_description}
            onChange={(e) => handleTextAreaChange(e)}
            className={styles.formTextArea}
          />
          <p className={styles.formTextLength}>
            <span
              className={notAdoptedDescriptionTextLength > 100 ? styles.formTextLengthError : ""}
            >
              {notAdoptedDescriptionTextLength}
            </span>
            /100文字以内
          </p>
          {errors.not_adopted_description && (
            <p className={styles.errorMessage}>{errors.not_adopted_description.message}</p>
          )}
        </div>
      </div>
      <div className={styles.formItemBox}>
        <hr className={styles.formLine} />
      </div>
      <div className={styles.formItemBox}>
        <h3 className={styles.formItem}>
          注意事項
          <Label theme="lightPink" className={styles.formRequiredLabel}>
            必須
          </Label>
        </h3>
        <div className={styles.formNoteBox}>
          <Notice
            isPayPerJobEntryFeeType={isPayPerJobEntryFeeType}
            recruitmentFeePath={recruitmentFeePath}
          />
          <div className={styles.formNoteCheckBox} id="not-adopted-form-checkbox">
            <Checkbox
              {...register("note", {
                required: "選択してください。",
              })}
              id="is_confirmed"
              checked={isChecked}
              hasError={!!errors.note}
              onChange={handleChangeCheckbox}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="is_confirmed" className={styles.formNoteCheckBoxLabel}>
              注意事項に同意する
            </label>
          </div>
          {errors.note && <p className={styles.errorMessage}>{errors.note.message}</p>}
        </div>
      </div>
    </div>
  );
};
